import { apiClient } from './apiClient';

export type ApplicationLayout = 'DEFAULT' | 'VERTICAL_MENU' | 'HOSPITALITY' | 'MODERN';

export interface VodHarvestingConfiguration {
  detectionEnabled: boolean;
  enforcementEnabled: boolean;
  shortWindowSeconds: number;
  requestWindowSeconds: number;
  longWindowMinutes: number;
  holdDurationMinutes: number;
  maxRequestsPerWindow: number;
  maxDistinctContentPerShortWindow: number;
  maxRequestsPerLongWindow: number;
  maxDistinctContentPerLongWindow: number;
}

export interface ApplicationSettings {
  layout: ApplicationLayout;
  vodHarvesting: VodHarvestingConfiguration;
  administration: AdministrativeSettings | null;
  android: AndroidSettings;
  themes: { id: string }[];
}

const ENDPOINT = '/settings/application';

export const applicationSettingsService = {
  getSettings(): Promise<ApplicationSettings> {
    return apiClient.get<ApplicationSettings>(ENDPOINT);
  },

  updateSettings(settings: ApplicationSettingsPatch): Promise<ApplicationSettings> {
    return apiClient.patch<ApplicationSettings>(ENDPOINT, settings);
  },
};

export interface AdministrativeSettings {
  id: string;
  premiereContentDays: number | null;
  recentContentDays: number | null;
  indexOnSaveOrUpdate: boolean | null;
  appName: string | null;
  appSubtitle: string | null;
  logoUrl: string | null;
  logoCompactUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  fontFamily: string | null;
  supportEmail: string | null;
  legalUrl: string | null;
  privacyUrl: string | null;
  categoriesEnabled: boolean | null;
  tvChannelsEnabled: boolean | null;
  tvGuideEnabled: boolean | null;
  radioStationsEnabled: boolean | null;
  documentariesEnabled: boolean | null;
  moviesEnabled: boolean | null;
  concertsEnabled: boolean | null;
  tvShowsEnabled: boolean | null;
  sportsEnabled: boolean | null;
  iptvProvidersEnabled: boolean | null;
  flussonicServersEnabled: boolean | null;
  channelAutomationsEnabled: boolean | null;
  channelScrapingEnabled: boolean | null;
  liveTvCountriesEnabled: boolean | null;
  radioCountriesEnabled: boolean | null;
  audienceManagementEnabled: boolean | null;
  adsEnabled: boolean | null;
  reportsEnabled: boolean | null;
  maxDemoPerMonth: number | null;
  demoDays: number | null;
}

export interface AndroidSettings {
  id: string;
  homeSlidesDuration: number | null;
  changePasswordEnabled: boolean | null;
  homeSlidesIndicatorPosition: 'DEFAULT' | 'TOP_CENTER' | null;
  fullScreenEnterActsAsBack: boolean | null;
  layout: ApplicationLayout | null;
  showChannelsOnCategoryFocus: boolean | null;
  channelCategoriesExpanded: boolean | null;
  categoryMenuOnPreviewMode: boolean | null;
  numericButtonInCategoryMenuOnPreviewMode: boolean | null;
  mixCategoriesAndCountriesAlphabetically: boolean | null;
  removeCountryGrouping: boolean | null;
  moviesLastNYears: number | null;
  moviesShowPremiere: boolean | null;
  moviesShowRecentlyAdded: boolean | null;
  tvShowsLastNYears: number | null;
  tvShowsShowPremiere: boolean | null;
  tvShowsShowRecentlyAdded: boolean | null;
  documentariesLastNYears: number | null;
  documentariesShowPremiere: boolean | null;
  documentariesShowRecentlyAdded: boolean | null;
  sportsLastNYears: number | null;
  sportsShowPremiere: boolean | null;
  sportsShowRecentlyAdded: boolean | null;
  concertsLastNYears: number | null;
  concertsShowPremiere: boolean | null;
  concertsShowRecentlyAdded: boolean | null;
  theme: { id: string } | null;
}

export interface ApplicationSettingsPatch {
  layout?: ApplicationLayout;
  vodHarvesting?: VodHarvestingConfiguration;
  administration?: Partial<Omit<AdministrativeSettings, 'id'>>;
  android?: Partial<Omit<AndroidSettings, 'id' | 'theme'>> & { themeId?: string };
}
